// Native delegation from the vault: the client side of
// docs/19-staking-plan.md (section 4.2 the lifecycle payloads, 4.4 the
// program's rules, 4.8 discovery, 6.3 the checks before signing). A one to
// one port of sdk/rust/src/stake.rs (same names in camelCase, same constants,
// same error cases, the same integer arithmetic in BigInt), pinned against it
// by sdk/fixtures/stake-v1.json (test/stake.test.ts).
//
// Every payload here is an ordinary EXACT Execute: inner instructions for the
// Stake program, System `AllocateWithSeed` and SPL Token `SyncNative` /
// `UnwrapLamports`, in the forms the program admits (program/src/allowlist.rs).
// Nothing in the wire format, header or digest is new (digest.ts).
//
// The warmup and cooldown arithmetic mirrors `solana-stake-interface`'s
// `Delegation::stake_activating_and_deactivating_v2` (integer basis-point
// allowance, SIMD-0391), which Stake `program@v5.1.0` calls with the new rate
// active from epoch 0, and the StakeHistory window of the program's
// `StakeHistorySysvar` lookup.
//
// Beyond stake.rs (TypeScript only, docs/19 4.4, 4.8 and 6.3 item 6): the
// RPC-facing discovery (`discoverStake`), the unsigned dry run
// (`dryRunInner`, with `epochOfSlot` over the bank's live schedule from
// `getEpochInfo`, `epochScheduleFromEpochInfo`; the EpochSchedule sysvar
// account is only compared, `checkEpochSchedule`) and the structural-rule
// mirror (`stakeRuleCheck`), which predicts the program's answer to an
// Execute before anything is signed.

import { sha256 } from '@noble/hashes/sha2.js';
import { type InnerIx, type SettleSpec, FLAGS, MAX_AUX, MAX_INNER_ACCOUNTS, MAX_INNER_IX, U64_MAX, fromHex, hex, jupiterSlots, u32le, u64le } from './digest.js';
import { concat } from './derive.js';
import { feeSide } from './jupiter.js';
import { equalBytes } from './recovery.js';
import { type OnChainAccount, RelayerError, type Rpc, type SimulationResult, toBase58 } from './relayer.js';
import { type Instruction, type Limits, SIGNATURE_SIZE, checkV1, compileV1, serializeV1 } from './v1.js';

/** `Stake11111111111111111111111111111111111111` (program/src/allowlist.rs `STAKE`). */
export const STAKE_PROGRAM: Uint8Array = fromHex('06a1d8179137542a983437bdfe2a7ab2557f535c8a78722b68a49dc000000000');
/** `11111111111111111111111111111111`. */
export const SYSTEM_PROGRAM: Uint8Array = new Uint8Array(32);
/** `TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA` (p-token; `UnwrapLamports` exists only here). */
export const TOKEN_PROGRAM: Uint8Array = fromHex('06ddf6e1d765a193d9cbe146ceeb79ac1cb485ed5f5b37913a8cf5857eff00a9');
/** `TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb`. */
export const TOKEN_2022_PROGRAM: Uint8Array = fromHex('06ddf6e1ee758fde18425dbce46ccddab61afc4d83b90d27febdf928d8a18bfc');
/** `JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4` (allowlist.rs `JUPITER_V6`). */
export const JUPITER_V6: Uint8Array = fromHex('0479d55bf231c06eee74c56ece681507fdb1b2dea3f48e5102b1cda256bc138f');
/** The space of a stake account, and what `AllocateWithSeed` must ask for (docs/19 4.4.3). */
export const STAKE_ACCOUNT_LEN = 200n;
/** Seeds `stake:0` .. `stake:63` (docs/19 4.2, 4.8): the discovery window. */
export const SEED_WINDOW = 64;
export const SEED_PREFIX = 'stake:';
/** A deactivation epoch of `u64::MAX` means "not deactivated". */
export const NOT_DEACTIVATED = U64_MAX;
/** StakeHistory keeps this many epochs; older entries have fallen off. */
export const HISTORY_MAX_ENTRIES = 512n;
/** SIMD-0391: 9% of the cluster's effective stake may warm up or cool down per epoch, in force from epoch 0 under Stake v5.1.0. */
export const WARMUP_COOLDOWN_RATE_BPS = 900n;
export const BASIS_POINTS_PER_UNIT = 10_000n;

/** SPL Token `SyncNative` tag (allowlist.rs `TOKEN_SYNC_NATIVE`). */
export const TOKEN_SYNC_NATIVE = 17;
/** p-token `UnwrapLamports` tag (docs/19 4.4.2). */
export const TOKEN_UNWRAP_LAMPORTS = 45;
export const TOKEN_CLOSE_ACCOUNT = 9;
export const TOKEN_TRANSFER_CHECKED = 12;
/** System `AllocateWithSeed` tag, u32 LE (docs/19 4.4.3). */
export const SYSTEM_ALLOCATE_WITH_SEED = 9;
/** Stake instruction tags, u32 LE (docs/19 4.4.4). */
export const STAKE_DELEGATE = 2;
export const STAKE_SPLIT = 3;
export const STAKE_WITHDRAW = 4;
export const STAKE_DEACTIVATE = 5;
export const STAKE_MERGE = 7;
export const STAKE_INITIALIZE_CHECKED = 9;

/** The authority bits of the vault's own stake account: staker, withdrawer, zero lockup. */
export const OWN_STAKE = 7;

const U128_MAX = (1n << 128n) - 1n;
const MAX_SEED_LEN = 32;
const PDA_MARKER = new TextEncoder().encode('ProgramDerivedAddress');

/**
 * The Rust `StakeError` cases (`SeedOutOfRange`, `ShortData`, `UnknownState`,
 * `MalformedHistory`), thrown rather than returned, plus the TypeScript-only
 * cases of the RPC-facing helpers: `BadSeed` (createWithSeed's own refusals),
 * `SnapshotMismatch` (the getProgramAccounts cross-check answered before
 * the snapshot's slot or more than `maxSlotGap` slots after it, docs/19 6.3
 * item 1), `CrossCheckMismatch` (the seed window and getProgramAccounts
 * disagree on a window account) and `BadEpochInfo` (a getEpochInfo answer that
 * describes no epoch: zero slots, or a slot index outside it).
 */
export type StakeErrorCode = 'SeedOutOfRange' | 'ShortData' | 'UnknownState' | 'MalformedHistory' | 'BadSeed' | 'SnapshotMismatch' | 'CrossCheckMismatch' | 'BadEpochInfo';

export class StakeError extends Error {
  constructor(
    readonly code: StakeErrorCode,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'StakeError';
  }
}

const satAdd = (a: bigint, b: bigint): bigint => (a + b > U64_MAX ? U64_MAX : a + b);
const satSub = (a: bigint, b: bigint): bigint => (a > b ? a - b : 0n);
const satMul128 = (a: bigint, b: bigint): bigint => (a * b > U128_MAX ? U128_MAX : a * b);
const minBig = (a: bigint, b: bigint): bigint => (a < b ? a : b);
const maxBig = (a: bigint, b: bigint): bigint => (a > b ? a : b);

// ----------------------------------------------------------------- addresses

/** `stake:<n>` in decimal without leading zeros, `0 <= n < 64` (docs/19 4.2). */
export function seed(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n >= SEED_WINDOW) throw new StakeError('SeedOutOfRange', `seed index ${n} outside 0..${SEED_WINDOW - 1}`, n);
  return `${SEED_PREFIX}${n}`;
}

/** `Address::create_with_seed`: sha256(base || seed || owner) over raw bytes; the seed is at most 32 bytes and the owner may not end with the PDA marker. */
export function createWithSeed(base: Uint8Array, seedText: string, owner: Uint8Array): Uint8Array {
  const s = new TextEncoder().encode(seedText);
  if (s.length > MAX_SEED_LEN) throw new StakeError('BadSeed', `seed is ${s.length} bytes, at most ${MAX_SEED_LEN}`);
  if (owner.length !== 32 || base.length !== 32) throw new StakeError('BadSeed', 'base and owner are 32 bytes');
  if (equalBytes(owner.subarray(32 - PDA_MARKER.length), PDA_MARKER)) throw new StakeError('BadSeed', 'owner ends with the PDA marker');
  return sha256(concat(base, s, owner));
}

/** `S_n = create_with_seed(vault, "stake:<n>", Stake)`: sha256(vault || seed || Stake) (docs/19 4.2). */
export function stakeAddress(vault: Uint8Array, n: number): Uint8Array {
  return createWithSeed(vault, seed(n), STAKE_PROGRAM);
}

// ------------------------------------------------------------------ encoders

const w = (a: Uint8Array) => ({ pubkey: a, isWritable: true });
const r = (a: Uint8Array) => ({ pubkey: a, isWritable: false });
const tag = (t: number) => u32le(t);

/**
 * The inner instructions one at a time, in the exact forms of docs/19
 * 4.4.2-4.4.4 (data length, meta count and the vault's position are what the
 * program checks). Metas carry the writable flag the target program needs;
 * `is_signer` is scrubbed on the wire and the program promotes the vault by
 * address.
 */
export const encode = {
  /** Token `SyncNative` `[17]`; `[account w]`. */
  syncNative(account: Uint8Array): InnerIx {
    return { program: TOKEN_PROGRAM, metas: [w(account)], data: Uint8Array.of(TOKEN_SYNC_NATIVE) };
  },
  /** Token `UnwrapLamports` `[45, 1, lamports u64 LE]`; `[source w, destination w, vault]` (docs/19 4.4.2). */
  unwrapLamports(source: Uint8Array, destination: Uint8Array, vault: Uint8Array, lamports: bigint): InnerIx {
    return { program: TOKEN_PROGRAM, metas: [w(source), w(destination), r(vault)], data: concat(Uint8Array.of(TOKEN_UNWRAP_LAMPORTS, 1), u64le(lamports)) };
  },
  /** System `AllocateWithSeed` `[9,0,0,0] base L seed space=200 owner=Stake`; `[stake w, vault]` (docs/19 4.4.3). */
  allocateWithSeed(stake: Uint8Array, vault: Uint8Array, seedText: string): InnerIx {
    const s = new TextEncoder().encode(seedText);
    return { program: SYSTEM_PROGRAM, metas: [w(stake), r(vault)], data: concat(tag(SYSTEM_ALLOCATE_WITH_SEED), vault, u64le(BigInt(s.length)), s, u64le(STAKE_ACCOUNT_LEN), STAKE_PROGRAM) };
  },
  /** Stake `InitializeChecked` `[9,0,0,0]`; `[stake w, staker=vault, withdrawer=vault]` (docs/19 4.4.4). */
  initializeChecked(stake: Uint8Array, vault: Uint8Array): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(stake), r(vault), r(vault)], data: tag(STAKE_INITIALIZE_CHECKED) };
  },
  /** Stake `DelegateStake` `[2,0,0,0]`; `[stake w, vote, staker=vault]`. */
  delegate(stake: Uint8Array, vote: Uint8Array, vault: Uint8Array): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(stake), r(vote), r(vault)], data: tag(STAKE_DELEGATE) };
  },
  /** Stake `Deactivate` `[5,0,0,0]`; `[stake w, staker=vault]`. */
  deactivate(stake: Uint8Array, vault: Uint8Array): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(stake), r(vault)], data: tag(STAKE_DEACTIVATE) };
  },
  /** Stake `Withdraw` `[4,0,0,0, lamports u64 LE]`; `[stake w, recipient w, withdrawer=vault]`. The recipient is the vault's wrapped-SOL account (or the vault itself). */
  withdraw(stake: Uint8Array, recipient: Uint8Array, vault: Uint8Array, lamports: bigint): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(stake), w(recipient), r(vault)], data: concat(tag(STAKE_WITHDRAW), u64le(lamports)) };
  },
  /** Stake `Split` `[3,0,0,0, lamports u64 LE]`; `[source w, destination w, staker=vault]`. */
  split(source: Uint8Array, destination: Uint8Array, vault: Uint8Array, lamports: bigint): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(source), w(destination), r(vault)], data: concat(tag(STAKE_SPLIT), u64le(lamports)) };
  },
  /** Stake `Merge` `[7,0,0,0]`; `[destination w, source w, staker=vault]`. */
  merge(destination: Uint8Array, source: Uint8Array, vault: Uint8Array): InnerIx {
    return { program: STAKE_PROGRAM, metas: [w(destination), w(source), r(vault)], data: tag(STAKE_MERGE) };
  },
};

// ------------------------------------------------------------- the payloads

/**
 * The vault, its wrapped-SOL associated token account (`ataAddress` of the
 * vault for the wSOL mint under SPL Token) and the seed index of the stake
 * account a payload acts on.
 */
export interface Position {
  vault: Uint8Array;
  ata: Uint8Array;
  n: number;
}

export function positionStake(p: Position): Uint8Array {
  return stakeAddress(p.vault, p.n);
}

/**
 * Stake into a new account (docs/19 4.2 row 1): SyncNative, UnwrapLamports of
 * `lamports` into `S_n`, AllocateWithSeed, InitializeChecked, DelegateStake.
 * `lamports >= Rent(200) + minimum delegation` (`checkStakeAmount`).
 */
export function stakeNew(p: Position, vote: Uint8Array, lamports: bigint): InnerIx[] {
  const s = positionStake(p);
  return [encode.syncNative(p.ata), encode.unwrapLamports(p.ata, s, p.vault, lamports), encode.allocateWithSeed(s, p.vault, seed(p.n)), encode.initializeChecked(s, p.vault), encode.delegate(s, vote, p.vault)];
}

/** Stake again into an existing, fully inactive account (docs/19 4.2 row 2): SyncNative, UnwrapLamports, DelegateStake. */
export function stakeAgain(p: Position, vote: Uint8Array, lamports: bigint): InnerIx[] {
  const s = positionStake(p);
  return [encode.syncNative(p.ata), encode.unwrapLamports(p.ata, s, p.vault, lamports), encode.delegate(s, vote, p.vault)];
}

/** Unstake (docs/19 4.2 row 3): Deactivate. */
export function unstake(p: Position): InnerIx[] {
  return [encode.deactivate(positionStake(p), p.vault)];
}

/**
 * Unstake part (docs/19 4.2 row 4, decision 3): UnwrapLamports of `rent200`
 * into a new `S_m`, AllocateWithSeed `S_m`, Split `lamports` of stake from
 * `S_n` into it, Deactivate `S_m`. Both parts must keep at least the minimum
 * delegation.
 */
export function unstakePart(p: Position, m: number, lamports: bigint, rent200: bigint): InnerIx[] {
  const s = positionStake(p);
  const t = stakeAddress(p.vault, m);
  return [encode.unwrapLamports(p.ata, t, p.vault, rent200), encode.allocateWithSeed(t, p.vault, seed(m)), encode.split(s, t, p.vault, lamports), encode.deactivate(t, p.vault)];
}

/** Merge (docs/19 4.2 row 5, decision 3): `S_source` into `S_n`; both vault-controlled and mergeable (both inactive, or both active on the same validator). */
export function mergeInto(p: Position, source: number): InnerIx[] {
  return [encode.merge(positionStake(p), stakeAddress(p.vault, source), p.vault)];
}

/**
 * Move to savings (docs/19 4.2 row 6, the default withdraw): Withdraw
 * `accountLamports - rent200` (saturating) to the wSOL account, then
 * SyncNative. The account stays open with its rent, so a donation cannot make
 * it fail.
 */
export function moveToSavings(p: Position, accountLamports: bigint, rent200: bigint): InnerIx[] {
  const s = positionStake(p);
  return [encode.withdraw(s, p.ata, p.vault, satSub(accountLamports, rent200)), encode.syncNative(p.ata)];
}

/** Close the stake account (docs/19 4.2 row 7, explicit): Withdraw everything, then SyncNative. Fails if a donation below `Rent(200)` lands first (the client warns). */
export function closePosition(p: Position, accountLamports: bigint): InnerIx[] {
  const s = positionStake(p);
  return [encode.withdraw(s, p.ata, p.vault, accountLamports), encode.syncNative(p.ata)];
}

/** Switch validator (docs/19 4.2 row 8): DelegateStake to another vote account once the effective stake is 0 (or the same vote account in the deactivation epoch). */
export function switchValidator(p: Position, vote: Uint8Array): InnerIx[] {
  return [encode.delegate(positionStake(p), vote, p.vault)];
}

/** Collect extra while staked (docs/19 4.2 row 9): Withdraw `lamports` above delegation plus rent, then SyncNative. */
export function collectExtra(p: Position, lamports: bigint): InnerIx[] {
  const s = positionStake(p);
  return [encode.withdraw(s, p.ata, p.vault, lamports), encode.syncNative(p.ata)];
}

// ------------------------------------------------------------------ decoding

/**
 * `StakeStateV2` as laid out in a 200-byte stake account (bincode, fixed
 * offsets): tag u32 at 0; `Meta` at 4 (rent reserve u64, staker 12..44,
 * withdrawer 44..76, lockup 76..124: unix_timestamp i64, epoch u64,
 * custodian); `Stake` at 124 (voter 124..156, stake 156..164, activation epoch
 * 164..172, deactivation epoch 172..180, the retired f64 rate 180..188,
 * credits observed 188..196); flags u8 at 196; padding to 200.
 */
export interface Lockup {
  unixTimestamp: bigint;
  epoch: bigint;
  custodian: Uint8Array;
}

export interface Meta {
  rentExemptReserve: bigint;
  staker: Uint8Array;
  withdrawer: Uint8Array;
  lockup: Lockup;
}

export interface StakeData {
  delegation: Delegation;
  creditsObserved: bigint;
}

export type StakeState = { kind: 'Uninitialized' } | { kind: 'Initialized'; meta: Meta } | { kind: 'Stake'; meta: Meta; stake: StakeData; flags: number } | { kind: 'RewardsPool' };

const view = (d: Uint8Array) => new DataView(d.buffer, d.byteOffset, d.byteLength);
const u64At = (d: Uint8Array, at: number): bigint => view(d).getBigUint64(at, true);
const keyAt = (d: Uint8Array, at: number): Uint8Array => d.slice(at, at + 32);

function need(d: Uint8Array, n: number): void {
  if (d.length < n) throw new StakeError('ShortData', `stake data is ${d.length} bytes, needs ${n}`, { len: d.length, need: n });
}

/** Decode a stake account's data. Trailing bytes beyond what the state needs are ignored, as the program's own bincode decode ignores them. */
export function decode(d: Uint8Array): StakeState {
  need(d, 4);
  const t = view(d).getUint32(0, true);
  const meta = (): Meta => {
    need(d, 124);
    return { rentExemptReserve: u64At(d, 4), staker: keyAt(d, 12), withdrawer: keyAt(d, 44), lockup: { unixTimestamp: view(d).getBigInt64(76, true), epoch: u64At(d, 84), custodian: keyAt(d, 92) } };
  };
  switch (t) {
    case 0:
      return { kind: 'Uninitialized' };
    case 1:
      return { kind: 'Initialized', meta: meta() };
    case 2: {
      need(d, 197);
      const m = meta();
      const delegation = new Delegation({ voter: keyAt(d, 124), stake: u64At(d, 156), activationEpoch: u64At(d, 164), deactivationEpoch: u64At(d, 172) });
      return { kind: 'Stake', meta: m, stake: { delegation, creditsObserved: u64At(d, 188) }, flags: d[196]! };
    }
    case 3:
      return { kind: 'RewardsPool' };
    default:
      throw new StakeError('UnknownState', `unknown stake state tag ${t}`, t);
  }
}

/**
 * The program's authority bits over a Stake-owned account (execute.rs
 * `stake_authority`): 1 = the staker (bytes 12..44) is the vault, 2 = the
 * withdrawer (44..76) is the vault, 4 = at least 124 bytes and the lockup
 * bytes 76..124 all zero. 0 for anything not Stake-owned or shorter than 76
 * bytes. I-S4 refuses any account with bit 1 or 2 set in a SETTLE Execute;
 * Split, Merge and the UnwrapLamports destination need all three (docs/19
 * 4.4.2, 4.4.4, 4.4.5).
 */
export function authorityBits(owner: Uint8Array, data: Uint8Array, vault: Uint8Array): number {
  if (!equalBytes(owner, STAKE_PROGRAM) || data.length < 76) return 0;
  let bits = 0;
  if (equalBytes(data.subarray(12, 44), vault)) bits |= 1;
  if (equalBytes(data.subarray(44, 76), vault)) bits |= 2;
  if (data.length >= 124 && data.subarray(76, 124).every((b) => b === 0)) bits |= 4;
  return bits;
}

/**
 * How a stake account relates to the vault (docs/19 4.8): `Owned` is the
 * user's to act on; `Named` (a gift, or an attacker-made account naming the
 * vault with a lockup) is listed read-only and never offered an action.
 */
export type Control = 'Owned' | 'Named' | 'Foreign';

export function control(owner: Uint8Array, data: Uint8Array, vault: Uint8Array): Control {
  const b = authorityBits(owner, data, vault);
  if (b === OWN_STAKE) return 'Owned';
  if (b & 3) return 'Named';
  return 'Foreign';
}

/** An account as `classify` sees it; `null` is an absent account. */
export interface AccountView {
  owner: Uint8Array;
  data: Uint8Array;
}

/**
 * What one seed slot of the 64-seed window holds (docs/19 4.8): `Free` if the
 * address is absent or a System account with no data (the next stake may use
 * it), otherwise its stake state (null when it does not decode) and control.
 */
export type Slot = { kind: 'Free' } | { kind: 'Occupied'; control: Control; state: StakeState | null };

export function classify(account: AccountView | null, vault: Uint8Array): Slot {
  if (!account) return { kind: 'Free' };
  if (equalBytes(account.owner, SYSTEM_PROGRAM) && account.data.length === 0) return { kind: 'Free' };
  let state: StakeState | null;
  try {
    state = decode(account.data);
  } catch {
    state = null;
  }
  return { kind: 'Occupied', control: control(account.owner, account.data, vault), state };
}

/** The lowest `n` in the window whose slot is free, given the window's accounts in seed order (`null` for absent); `null` when none is. */
export function nextFreeSeed(window: (AccountView | null)[], vault: Uint8Array): number | null {
  const i = window.slice(0, SEED_WINDOW).findIndex((a) => classify(a, vault).kind === 'Free');
  return i < 0 ? null : i;
}

// --------------------------------------------------- warmup and cooldown

export interface HistoryEntry {
  effective: bigint;
  activating: bigint;
  deactivating: bigint;
}

/** The StakeHistory sysvar as the Stake program reads it in `currentEpoch`: entries for epochs `current - 512 ..= current - 1`, newest first. */
export class StakeHistory {
  constructor(
    readonly currentEpoch: bigint,
    readonly entries: [bigint, HistoryEntry][],
  ) {}

  /** Parse the sysvar's bincode bytes: `u64 len || (epoch u64, effective, activating, deactivating)*`. */
  static parse(currentEpoch: bigint, data: Uint8Array): StakeHistory {
    if (data.length < 8) throw new StakeError('MalformedHistory', 'StakeHistory shorter than its length prefix');
    const n = u64At(data, 0);
    if (BigInt(data.length) !== 8n + 32n * n) throw new StakeError('MalformedHistory', `StakeHistory of ${data.length} bytes for ${n} entries`);
    const entries: [bigint, HistoryEntry][] = [];
    for (let i = 0; i < Number(n); i++) {
      const at = 8 + 32 * i;
      entries.push([u64At(data, at), { effective: u64At(data, at + 8), activating: u64At(data, at + 16), deactivating: u64At(data, at + 24) }]);
    }
    return new StakeHistory(currentEpoch, entries);
  }

  /** The program's `StakeHistorySysvar::get_entry`: `null` for the current epoch or later, and for epochs older than the 512 the sysvar keeps. */
  getEntry(target: bigint): HistoryEntry | null {
    if (this.currentEpoch === 0n) return null;
    const newest = this.currentEpoch - 1n;
    const oldest = satSub(this.currentEpoch, HISTORY_MAX_ENTRIES);
    if (target < oldest || target > newest) return null;
    const hit = this.entries.find(([e]) => e === target);
    return hit ? hit[1] : null;
  }
}

/**
 * `warmup_cooldown_allowance::calculate_stake_change_allowance` with the 900
 * bps rate: `account * cluster_effective * 900 / (cluster_portion * 10_000)`,
 * in u128, saturating, capped at `account`.
 */
export function allowance(accountPortion: bigint, clusterPortion: bigint, clusterEffective: bigint): bigint {
  if (accountPortion === 0n || clusterPortion === 0n || clusterEffective === 0n) return 0n;
  const numerator = satMul128(satMul128(accountPortion, clusterEffective), WARMUP_COOLDOWN_RATE_BPS);
  const denominator = satMul128(clusterPortion, BASIS_POINTS_PER_UNIT);
  return minBig(numerator / denominator, accountPortion);
}

export interface Activation {
  effective: bigint;
  activating: bigint;
  deactivating: bigint;
}

/** A stake account's delegation, with the v2 warmup and cooldown arithmetic (docs/19 6.3 item 3). */
export class Delegation {
  readonly voter: Uint8Array;
  readonly stake: bigint;
  readonly activationEpoch: bigint;
  readonly deactivationEpoch: bigint;

  constructor(d: { voter?: Uint8Array; stake: bigint; activationEpoch: bigint; deactivationEpoch: bigint }) {
    this.voter = d.voter ?? new Uint8Array(32);
    this.stake = d.stake;
    this.activationEpoch = d.activationEpoch;
    this.deactivationEpoch = d.deactivationEpoch;
  }

  isBootstrap(): boolean {
    return this.activationEpoch === U64_MAX;
  }

  /** `(effective, activating)` at `targetEpoch`: `stake_and_activating_v2`. */
  private stakeAndActivating(targetEpoch: bigint, history: StakeHistory): [bigint, bigint] {
    const delegated = this.stake;
    if (this.isBootstrap()) return [delegated, 0n];
    if (this.activationEpoch === this.deactivationEpoch) return [0n, 0n];
    if (targetEpoch === this.activationEpoch) return [0n, delegated];
    if (targetEpoch < this.activationEpoch) return [0n, 0n];
    let prev = history.getEntry(this.activationEpoch);
    // No history: assume fully effective.
    if (!prev) return [delegated, 0n];
    let prevEpoch = this.activationEpoch;
    let activated = 0n;
    for (;;) {
      const current = prevEpoch + 1n;
      if (prev.activating === 0n) break;
      const newly = allowance(delegated - activated, prev.activating, prev.effective);
      activated += maxBig(newly, 1n);
      if (activated >= delegated) {
        activated = delegated;
        break;
      }
      if (current >= targetEpoch || current >= this.deactivationEpoch) break;
      const e = history.getEntry(current);
      if (!e) break;
      prevEpoch = current;
      prev = e;
    }
    return [activated, delegated - activated];
  }

  /** `stake_activating_and_deactivating_v2` at `targetEpoch`. */
  activatingAndDeactivating(targetEpoch: bigint, history: StakeHistory): Activation {
    const [effective, activating] = this.stakeAndActivating(targetEpoch, history);
    if (targetEpoch < this.deactivationEpoch) return { effective, activating, deactivating: 0n };
    if (targetEpoch === this.deactivationEpoch) return { effective, activating: 0n, deactivating: effective };
    let prev = history.getEntry(this.deactivationEpoch);
    // No history: assume fully deactivated.
    if (!prev) return { effective: 0n, activating: 0n, deactivating: 0n };
    let prevEpoch = this.deactivationEpoch;
    let remaining = effective;
    for (;;) {
      const current = prevEpoch + 1n;
      if (prev.deactivating === 0n) break;
      const newly = allowance(remaining, prev.deactivating, prev.effective);
      remaining = satSub(remaining, maxBig(newly, 1n));
      if (remaining === 0n) break;
      if (current >= targetEpoch) break;
      const e = history.getEntry(current);
      if (!e) break;
      prevEpoch = current;
      prev = e;
    }
    return { effective: remaining, activating: 0n, deactivating: remaining };
  }

  /** `stake_v2`: the effective stake at `epoch`. */
  effective(epoch: bigint, history: StakeHistory): bigint {
    return this.activatingAndDeactivating(epoch, history).effective;
  }
}

// --------------------------------------------------------- before signing

/**
 * What a Withdraw may take from a stake account (Stake `program@v5.1.0`
 * `process_withdraw`, lines 762-838; docs/19 6.3 item 3): `staked` is the
 * delegation while the account is not deactivated (activation included), the
 * effective stake from StakeHistory once `epoch >= deactivation_epoch`, and 0
 * for an Initialized account; a partial Withdraw must leave `staked + rent`
 * (`rent` = `Rent(200)` read live, not the stored reserve); a full Withdraw
 * (`W == lamports`) is refused while `staked != 0`.
 */
export interface WithdrawLimits {
  staked: bigint;
  /** The most a partial Withdraw may take. */
  partialMax: bigint;
  /** Whether `W == lamports` (the explicit close) can land. */
  fullAllowed: boolean;
}

export function withdrawLimits(state: StakeState, lamports: bigint, rent200: bigint, epoch: bigint, history: StakeHistory): WithdrawLimits {
  let staked = 0n;
  if (state.kind === 'Stake') {
    const d = state.stake.delegation;
    staked = epoch >= d.deactivationEpoch ? d.effective(epoch, history) : d.stake;
  }
  const reserve = state.kind === 'Uninitialized' ? 0n : satAdd(staked, rent200);
  return { staked, partialMax: satSub(lamports, reserve), fullAllowed: staked === 0n };
}

/**
 * Why the client refuses to sign (docs/19 6.3). The first eight are the Rust
 * `Precheck` variants; `DryRunFailed` and `EpochChanged` are the dry run's
 * verdicts (6.3 item 6, TypeScript only).
 */
export type Precheck =
  /** `X < Rent(200) + minimum delegation`. */
  | { kind: 'BelowMinimum'; need: bigint }
  /** `X > ATA lamports - Rent(165)`. */
  | { kind: 'ExceedsSavings'; available: bigint }
  /** `W` above `partialMax`, or a full Withdraw while staked. */
  | { kind: 'WithdrawTooLarge'; limit: bigint }
  /** The seed address holds something other than nothing or an empty System account. */
  | { kind: 'SeedOccupied' }
  /** Not a vault-controlled stake account (docs/19 4.8). */
  | { kind: 'NotControlled' }
  /** DelegateStake needs effective stake 0, or the rescind case. */
  | { kind: 'StillActive'; effective: bigint }
  /** Deactivate needs a not-yet-deactivated account. */
  | { kind: 'AlreadyDeactivated' }
  /** EpochRewards is active, or fewer than `margin` slots remain in the epoch. */
  | { kind: 'RewardWindow' }
  /** The unsigned dry run returned an error. */
  | { kind: 'DryRunFailed'; err: unknown }
  /** The dry run's context slot is in another epoch than the snapshot's. */
  | { kind: 'EpochChanged'; snapshotEpoch: bigint; contextEpoch: bigint };

/** The Rust `Result<T, Precheck>`. */
export type Checked<T = void> = { ok: true; value: T } | { ok: false; error: Precheck };
const ok = <T>(value: T): Checked<T> => ({ ok: true, value });
const refuse = <T = void>(error: Precheck): Checked<T> => ({ ok: false, error });

/** docs/19 6.3 item 3, staking: `X >= Rent(200) + minimum delegation` and `X <= ATA lamports - Rent(165)`. */
export function checkStakeAmount(x: bigint, rent200: bigint, minDelegation: bigint, ataLamports: bigint, rent165: bigint): Checked {
  const needed = satAdd(rent200, minDelegation);
  if (x < needed) return refuse({ kind: 'BelowMinimum', need: needed });
  const available = satSub(ataLamports, rent165);
  if (x > available) return refuse({ kind: 'ExceedsSavings', available });
  return ok(undefined);
}

/** docs/19 6.3 item 3, withdrawing `w` of `lamports` under `limits`. */
export function checkWithdrawAmount(w: bigint, lamports: bigint, limits: WithdrawLimits): Checked {
  if (w === lamports) return limits.fullAllowed ? ok(undefined) : refuse({ kind: 'WithdrawTooLarge', limit: limits.partialMax });
  if (w > limits.partialMax) return refuse({ kind: 'WithdrawTooLarge', limit: limits.partialMax });
  return ok(undefined);
}

/** docs/19 6.3 item 4: a new `S_n` is absent or a System account with no data. */
export function checkNewSeed(account: AccountView | null, vault: Uint8Array): Checked {
  return classify(account, vault).kind === 'Free' ? ok(undefined) : refuse({ kind: 'SeedOccupied' });
}

/** docs/19 6.3 item 4: every other action needs a vault-controlled account. */
export function checkControlled(owner: Uint8Array, data: Uint8Array, vault: Uint8Array): Checked {
  return control(owner, data, vault) === 'Owned' ? ok(undefined) : refuse({ kind: 'NotControlled' });
}

/**
 * docs/19 6.3 item 4, DelegateStake on an existing account: effective stake
 * 0, or the rescind case (the same vote account, deactivated this epoch; Stake
 * `redelegate_stake`). An Initialized account delegates freely.
 */
export function checkDelegate(state: StakeState, vote: Uint8Array, epoch: bigint, history: StakeHistory): Checked {
  if (state.kind === 'Stake') {
    const d = state.stake.delegation;
    const effective = d.effective(epoch, history);
    if (effective === 0n || (d.deactivationEpoch === epoch && equalBytes(d.voter, vote))) return ok(undefined);
    return refuse({ kind: 'StillActive', effective });
  }
  if (state.kind === 'Initialized') return ok(undefined);
  return refuse({ kind: 'NotControlled' });
}

/** docs/19 6.3 item 4, Deactivate: the deactivation epoch is `u64::MAX`. */
export function checkDeactivate(state: StakeState): Checked {
  if (state.kind !== 'Stake') return refuse({ kind: 'NotControlled' });
  return state.stake.delegation.deactivationEpoch === NOT_DEACTIVATED ? ok(undefined) : refuse({ kind: 'AlreadyDeactivated' });
}

/** The EpochRewards sysvar's `active` flag (byte 80 of its 81 bytes); `null` when the data is shorter. */
export function epochRewardsActive(data: Uint8Array): boolean | null {
  return data.length > 80 ? data[80] !== 0 : null;
}

/**
 * docs/19 6.3 item 2: `valid_until_slot = min(slot + 150, first_slot_of_next_epoch - 1 - margin)`,
 * refused when EpochRewards is active or fewer than `margin` slots remain.
 */
export function rewardWindow(active: boolean, slot: bigint, firstSlotOfNextEpoch: bigint, margin: bigint): Checked<bigint> {
  if (active || satSub(firstSlotOfNextEpoch, slot) <= margin) return refuse({ kind: 'RewardWindow' });
  return ok(minBig(slot + 150n, firstSlotOfNextEpoch - 1n - margin));
}

// ------------------------------------------------------ the epoch schedule

/** Agave `MINIMUM_SLOTS_PER_EPOCH`. */
export const MINIMUM_SLOTS_PER_EPOCH = 32n;

/**
 * The EpochSchedule sysvar's shape (33 bytes, bincode), also the form the
 * bank's live schedule takes (`epochScheduleFromEpochInfo`). The epoch
 * boundary a client signs against comes from `getEpochInfo`; the sysvar
 * account is read for the record and compared (`checkEpochSchedule`).
 */
export interface EpochSchedule {
  slotsPerEpoch: bigint;
  leaderScheduleSlotOffset: bigint;
  warmup: boolean;
  firstNormalEpoch: bigint;
  firstNormalSlot: bigint;
}

/** slots_per_epoch u64 at 0, leader_schedule_slot_offset u64 at 8, warmup bool at 16, first_normal_epoch u64 at 17, first_normal_slot u64 at 25. */
export function parseEpochSchedule(data: Uint8Array): EpochSchedule {
  need(data, 33);
  return { slotsPerEpoch: u64At(data, 0), leaderScheduleSlotOffset: u64At(data, 8), warmup: data[16] !== 0, firstNormalEpoch: u64At(data, 17), firstNormalSlot: u64At(data, 25) };
}

const bitLength = (x: bigint): number => x.toString(2).length;

/**
 * Agave `EpochSchedule::get_epoch` (`get_epoch_and_slot_index`): below
 * `first_normal_slot` the warmup epochs double from 32 slots; after it,
 * `first_normal_epoch + (slot - first_normal_slot) / slots_per_epoch`. On
 * mainnet (warmup false, first normal slot 0) this is `slot / slots_per_epoch`.
 *
 * Without warmup, a slot below `first_normal_slot` (which a real sysvar
 * without warmup never has, its first normal slot being 0, but the
 * getEpochInfo form does: its first normal slot is the current epoch's
 * first) lies in an earlier epoch of the same length: floor division, so
 * the slot one before the boundary is the previous epoch. The Agave warmup
 * branch is kept for warmup true.
 */
export function epochOfSlot(slot: bigint, s: EpochSchedule): bigint {
  if (slot < s.firstNormalSlot) {
    if (!s.warmup) {
      if (s.slotsPerEpoch === 0n) return s.firstNormalEpoch;
      const back = (s.firstNormalSlot - slot + s.slotsPerEpoch - 1n) / s.slotsPerEpoch;
      return back >= s.firstNormalEpoch ? 0n : s.firstNormalEpoch - back;
    }
    // (slot + 32 + 1).next_power_of_two().trailing_zeros() - 32.trailing_zeros() - 1
    const x = slot + MINIMUM_SLOTS_PER_EPOCH + 1n;
    const pow2Exp = (x & (x - 1n)) === 0n ? bitLength(x) - 1 : bitLength(x);
    return BigInt(Math.max(0, pow2Exp - 5 - 1));
  }
  if (s.slotsPerEpoch === 0n) return s.firstNormalEpoch;
  return s.firstNormalEpoch + (slot - s.firstNormalSlot) / s.slotsPerEpoch;
}

/**
 * Agave `EpochSchedule::get_first_slot_in_epoch`, for `rewardWindow`'s first
 * slot of the next epoch. Without warmup every epoch has `slots_per_epoch`
 * slots, before `first_normal_epoch` too (the getEpochInfo form; for a real
 * sysvar without warmup, first normal epoch and slot 0, this is Agave's
 * answer unchanged).
 */
export function firstSlotInEpoch(epoch: bigint, s: EpochSchedule): bigint {
  if (!s.warmup) {
    const first = (epoch - s.firstNormalEpoch) * s.slotsPerEpoch + s.firstNormalSlot;
    return first < 0n ? 0n : first;
  }
  if (epoch <= s.firstNormalEpoch) return ((1n << epoch) - 1n) * MINIMUM_SLOTS_PER_EPOCH;
  return (epoch - s.firstNormalEpoch) * s.slotsPerEpoch + s.firstNormalSlot;
}

/** Agave `EpochSchedule::get_slots_in_epoch` (the doubling warmup epochs, then `slots_per_epoch`). */
function slotsInEpochOf(epoch: bigint, s: EpochSchedule): bigint {
  return s.warmup && epoch < s.firstNormalEpoch ? MINIMUM_SLOTS_PER_EPOCH << epoch : s.slotsPerEpoch;
}

/** The `getEpochInfo` fields the schedule needs, as numbers (the RPC's JSON) or bigints. */
export interface EpochInfoLike {
  epoch: bigint | number;
  absoluteSlot: bigint | number;
  slotIndex: bigint | number;
  slotsInEpoch: bigint | number;
}

/**
 * docs/19 6.3 items 1 and 2: the bank's live schedule from `getEpochInfo`,
 * in the EpochSchedule shape: `slotsPerEpoch` = `slotsInEpoch`, warmup
 * false, `firstNormalEpoch` = `epoch` and `firstNormalSlot` = `absoluteSlot -
 * slotIndex` (the current epoch's first slot), `leaderScheduleSlotOffset`
 * (which getEpochInfo does not report and nothing here reads) =
 * `slotsInEpoch`, as Agave's `EpochSchedule::new` sets it. Then
 * `firstSlotInEpoch(epoch + 1)` is the next boundary and `epochOfSlot`
 * places a slot on either side of the current epoch's start.
 *
 * The boundary comes from here, from the bank, never from the EpochSchedule
 * sysvar account on its own: devnet's account holds a stale genesis value
 * (8,192 slots per epoch, first normal slot 0) that its bank does not use
 * (getEpochInfo and getEpochSchedule both report 432,000; ST5, 2026-10-05),
 * and a client that derived the boundary from it refused every action. The
 * sysvar is still read into the snapshot for the record and compared with
 * `checkEpochSchedule`; a disagreement is recorded, never fatal, and the
 * bank's figure wins. The caller also requires the snapshot's Clock epoch to
 * equal `info.epoch` (one snapshot, docs/19 6.3 item 1).
 */
export function epochScheduleFromEpochInfo(info: EpochInfoLike): EpochSchedule {
  const epoch = BigInt(info.epoch);
  const absoluteSlot = BigInt(info.absoluteSlot);
  const slotIndex = BigInt(info.slotIndex);
  const slotsInEpoch = BigInt(info.slotsInEpoch);
  if (slotsInEpoch <= 0n || slotIndex < 0n || slotIndex >= slotsInEpoch || slotIndex > absoluteSlot) {
    throw new StakeError('BadEpochInfo', `getEpochInfo describes no epoch: slot index ${slotIndex} of ${slotsInEpoch} at slot ${absoluteSlot}`, { epoch, absoluteSlot, slotIndex, slotsInEpoch });
  }
  return { slotsPerEpoch: slotsInEpoch, leaderScheduleSlotOffset: slotsInEpoch, warmup: false, firstNormalEpoch: epoch, firstNormalSlot: absoluteSlot - slotIndex };
}

/**
 * The EpochSchedule sysvar account against the bank's live schedule
 * (`getEpochInfo`): its slots in the epoch the sysvar places `absoluteSlot`
 * in (its `slotsPerEpoch` after warmup) against `slotsInEpoch`, that epoch
 * against `info.epoch`, and the first slot it gives `info.epoch` against
 * `absoluteSlot - slotIndex`. For the record only: the boundary comes from
 * `getEpochInfo` (`epochScheduleFromEpochInfo`), a disagreement is recorded
 * in `note` (sysvar figure first, then getEpochInfo's), never fatal, and the
 * bank's figure wins.
 */
export function checkEpochSchedule(sysvar: EpochSchedule, info: EpochInfoLike): { agrees: boolean; note?: string } {
  const live = epochScheduleFromEpochInfo(info);
  const slot = BigInt(info.absoluteSlot);
  const epoch = BigInt(info.epoch);
  const predicted = epochOfSlot(slot, sysvar);
  const perEpoch = slotsInEpochOf(predicted, sysvar);
  const first = firstSlotInEpoch(epoch, sysvar);
  const parts: string[] = [];
  if (perEpoch !== live.slotsPerEpoch) parts.push(`${perEpoch} vs ${live.slotsPerEpoch} slots per epoch`);
  if (predicted !== epoch) parts.push(`epoch ${predicted} vs ${epoch} at slot ${slot}`);
  if (first !== live.firstNormalSlot) parts.push(`epoch ${epoch} starts at slot ${first} vs ${live.firstNormalSlot}`);
  return parts.length ? { agrees: false, note: `sysvar account disagrees: ${parts.join('; ')} (the sysvar's figure first, getEpochInfo's second, which wins)` } : { agrees: true };
}

// ------------------------------------------------------------- discovery

/** One stake account found for the vault: its address, its seed index (`null` outside the 64-seed window), lamports, decoded state (null if it does not decode) and control. */
export interface StakePosition {
  address: Uint8Array;
  n: number | null;
  lamports: bigint;
  state: StakeState | null;
  control: Control;
}

export interface Discovery {
  /** The snapshot's slot: the context slot of the one getMultipleAccounts call (the seed window and the extras). The dry run's `minContextSlot`. */
  contextSlot: number;
  /** The cross-check's slot: the context slot getProgramAccounts answered at, `contextSlot <= gpaSlot <= contextSlot + maxSlotGap`. */
  gpaSlot: number;
  /** Staker = withdrawer = vault, zero lockup: the user's to act on. */
  owned: StakePosition[];
  /** Naming the vault as staker or withdrawer but not owned (a gift, or a lockup): listed read-only, never offered an action. */
  named: StakePosition[];
  /** Seed slots holding something that is neither free nor naming the vault: they only block their seed. */
  foreign: { address: Uint8Array; n: number }[];
  /** The lowest free seed, or null when all 64 are taken. */
  nextFreeSeed: number | null;
  /** The `extra` accounts read in the same getMultipleAccounts call, in order. */
  extra: (OnChainAccount | null)[];
}

export type DiscoveryRpc = Pick<Rpc, 'getMultipleAccounts' | 'getProgramAccounts'>;

/**
 * The bound on how many slots after the snapshot (`getMultipleAccounts`) the
 * discovery cross-check (`getProgramAccounts`) may answer: 150, a blockhash's
 * validity. Within it, a stake account whose withdrawer is the vault, which
 * only the vault's own transactions or donations can change, is taken as
 * unchanged once the two reads agree on it byte for byte (docs/19 4.8, 6.3
 * item 1).
 */
export const DISCOVERY_MAX_SLOT_GAP = 150;

/** The wait before a discovery read is repeated after an RPC node answered -32016 (behind the minimum context slot), times the attempt number. */
export const DISCOVERY_RETRY_DELAY_MS = 400;

/** JSON-RPC -32016, "Minimum context slot has not been reached": the node that answered is behind the slot the read asked for. */
export function isBehindMinContextSlot(e: unknown): boolean {
  if (!(e instanceof RelayerError) || e.code !== 'RpcError') return false;
  const code = (e.detail as { code?: unknown } | undefined)?.code;
  return code === -32016 || /minimum context slot has not been reached/i.test(e.message);
}

/**
 * docs/19 4.8: find the vault's stake again from the vault address alone.
 *
 * Read 1 is one `getMultipleAccounts` over the 64 seed addresses `S_0..S_63`
 * (classified with `classify`; the lowest free seed is the next stake's).
 * Up to 36 `extra` addresses share that call and so that snapshot: Clock,
 * EpochSchedule, EpochRewards, Rent, StakeHistory, the vault, its wSOL ATA
 * and the vote account (docs/19 6.3 item 1 asks for exactly that one call).
 * Read 2, the cross-check, is `getProgramAccounts(Stake, dataSize 200,
 * memcmp offset 44 = vault)`: it finds accounts outside the window whose
 * withdrawer is the vault. It is a second call (a different RPC method) and
 * on a live cluster lands a slot or more after read 1 (devnet, 2026-10-05:
 * one slot apart, docs/20 section 4, F2), so it is asked with `minContextSlot`
 * = read 1's slot and accepted when it answered 0 to `maxSlotGap` slots
 * after read 1 (default `DISCOVERY_MAX_SLOT_GAP`, 150). A node behind the asked
 * slot answers -32016 to either read (seen on mainnet 2026-10-10 on a
 * load-balanced public endpoint); the attempt waits `retryDelayMs` times the
 * attempt number and reads both again, like a mismatch. Within that bound
 * the two reads must agree byte for byte on every window account whose
 * withdrawer is the vault, or `CrossCheckMismatch`: that comparison is the
 * consistency test. A pair outside the bound, or whose data disagree, is
 * read again (`attempts`, default 3); the last attempt's refusal is thrown
 * (`SnapshotMismatch` for the gap, `CrossCheckMismatch` for the data).
 * Read 1's slot stays the snapshot's (`contextSlot`, the dry run's
 * `minContextSlot`); the cross-check's is `gpaSlot`.
 */
export async function discoverStake(
  rpc: DiscoveryRpc,
  vault: Uint8Array,
  opts: { extra?: string[]; commitment?: string; minContextSlot?: number; attempts?: number; maxSlotGap?: number; retryDelayMs?: number; sleepImpl?: (ms: number) => Promise<void> } = {},
): Promise<Discovery> {
  const extra = opts.extra ?? [];
  if (SEED_WINDOW + extra.length > 100) throw new StakeError('CrossCheckMismatch', `getMultipleAccounts takes at most 100 addresses, asked for ${SEED_WINDOW + extra.length}`);
  const window = Array.from({ length: SEED_WINDOW }, (_, n) => stakeAddress(vault, n));
  const attempts = opts.attempts ?? 3;
  const maxSlotGap = opts.maxSlotGap ?? DISCOVERY_MAX_SLOT_GAP;
  const retryDelayMs = opts.retryDelayMs ?? DISCOVERY_RETRY_DELAY_MS;
  const sleep = opts.sleepImpl ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let refusal: StakeError | undefined;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    let multi: Awaited<ReturnType<DiscoveryRpc['getMultipleAccounts']>>;
    let gpa: Awaited<ReturnType<DiscoveryRpc['getProgramAccounts']>>;
    try {
      multi = await rpc.getMultipleAccounts([...window.map(toBase58), ...extra], { commitment: opts.commitment, minContextSlot: opts.minContextSlot });
      gpa = await rpc.getProgramAccounts(toBase58(STAKE_PROGRAM), [{ dataSize: Number(STAKE_ACCOUNT_LEN) }, { memcmp: { offset: 44, bytes: toBase58(vault) } }], { commitment: opts.commitment, minContextSlot: multi.slot });
    } catch (e) {
      // A node behind the asked slot (a load-balanced public endpoint; mainnet, 2026-10-10): wait for it, or for
      // another node, and read both again. Any other error, and the last attempt's, is the caller's.
      if (isBehindMinContextSlot(e) && attempt < attempts) {
        await sleep(retryDelayMs * attempt);
        continue;
      }
      throw e;
    }
    const gap = gpa.slot - multi.slot;
    // Written so that a NaN bound or slot refuses rather than accepts.
    if (!(gap >= 0 && gap <= maxSlotGap)) {
      const detail = { snapshotSlot: multi.slot, gpaSlot: gpa.slot, maxSlotGap, attempts };
      refusal = new StakeError('SnapshotMismatch', `getMultipleAccounts (the snapshot) answered at slot ${multi.slot} and getProgramAccounts (the cross-check) at ${gpa.slot}, a gap of ${gap} slots outside the bound 0..${maxSlotGap}, on the last of ${attempts} attempts`, detail);
      continue;
    }
    try {
      return assembleDiscovery(vault, window, multi.slot, gpa.slot, multi.accounts.slice(0, SEED_WINDOW), multi.accounts.slice(SEED_WINDOW), gpa.accounts);
    } catch (e) {
      if (!(e instanceof StakeError && e.code === 'CrossCheckMismatch')) throw e;
      refusal = e;
    }
  }
  throw refusal ?? new StakeError('SnapshotMismatch', `no discovery attempt was made (attempts = ${attempts})`);
}

function assembleDiscovery(vault: Uint8Array, window: Uint8Array[], contextSlot: number, gpaSlot: number, accounts: (OnChainAccount | null)[], extra: (OnChainAccount | null)[], gpa: { pubkey: Uint8Array; account: OnChainAccount }[]): Discovery {
  if (accounts.length !== SEED_WINDOW) throw new StakeError('CrossCheckMismatch', `getMultipleAccounts returned ${accounts.length} window accounts`);
  const owned: StakePosition[] = [];
  const named: StakePosition[] = [];
  const foreign: { address: Uint8Array; n: number }[] = [];
  const byAddress = new Map(gpa.map((g) => [hex(g.pubkey), g.account] as const));
  const windowHex = new Set(window.map(hex));
  accounts.forEach((a, n) => {
    const address = window[n]!;
    const listed = byAddress.get(hex(address));
    const withdrawerIsVault = !!a && equalBytes(a.owner, STAKE_PROGRAM) && BigInt(a.data.length) === STAKE_ACCOUNT_LEN && equalBytes(a.data.subarray(44, 76), vault);
    if (withdrawerIsVault !== !!listed || (listed && a && !equalBytes(listed.data, a.data))) {
      throw new StakeError('CrossCheckMismatch', `seed ${n} (${toBase58(address)}) disagrees between getMultipleAccounts and getProgramAccounts`);
    }
    const slot = classify(a, vault);
    if (slot.kind === 'Free') return;
    const p: StakePosition = { address, n, lamports: a!.lamports, state: slot.state, control: slot.control };
    if (slot.control === 'Owned') owned.push(p);
    else if (slot.control === 'Named') named.push(p);
    else foreign.push({ address, n });
  });
  for (const g of gpa) {
    if (windowHex.has(hex(g.pubkey))) continue;
    if (!equalBytes(g.account.owner, STAKE_PROGRAM) || !equalBytes(g.account.data.subarray(44, 76), vault)) throw new StakeError('CrossCheckMismatch', `getProgramAccounts returned ${toBase58(g.pubkey)}, which does not match its filters`);
    const slot = classify(g.account, vault);
    if (slot.kind === 'Free') continue;
    const p: StakePosition = { address: g.pubkey, n: null, lamports: g.account.lamports, state: slot.state, control: slot.control };
    (slot.control === 'Owned' ? owned : named).push(p);
  }
  return { contextSlot, gpaSlot, owned, named, foreign, nextFreeSeed: nextFreeSeed(accounts, vault), extra };
}

// ------------------------------------------------------- the unsigned dry run

/** The app's `TOKEN_TOUCHING_LIMITS` (app/src/engine.ts): 200,000 CU and 64 MiB of loaded data, for the dry run (docs/19 6.3 item 7). */
export const DRY_RUN_LIMITS: Limits = { computeUnitLimit: 200_000, loadedAccountsDataSizeLimit: 64 * 1024 * 1024, priorityFeeLamports: 0n };

export interface DryRunResult {
  ok: boolean;
  err: unknown;
  logs: string[];
  unitsConsumed: number | undefined;
  /** The simulateTransaction response's context slot. */
  contextSlot: number;
  /** The unsigned transaction that was simulated (v1 message || zeroed signatures). */
  wire: Uint8Array;
}

/** The payload's inner instructions as top-level instructions, the vault PDA marked as a signer wherever it is a meta (docs/19 6.3 item 6). */
export function dryRunInstructions(inner: InnerIx[], vault: Uint8Array): Instruction[] {
  return inner.map((ix) => ({ programId: ix.program, accounts: ix.metas.map((m) => ({ pubkey: m.pubkey, isSigner: equalBytes(m.pubkey, vault), isWritable: m.isWritable })), data: ix.data }));
}

/**
 * docs/19 6.3 item 6: simulate the payload's inner instructions as top-level
 * instructions, the relayer as fee payer and the vault PDA as a (readonly)
 * signer, in a v1 transaction whose signatures are zeroed (the vault cannot
 * sign and nothing is signed: no leaf is revealed, compatible with R11):
 * `simulateTransaction` with `sigVerify: false`, `replaceRecentBlockhash:
 * true`, encoding base64 and `minContextSlot` = the snapshot's slot. The
 * caller refuses to sign when `err` is set or the context slot is in another
 * epoch (`checkDryRun`). It does not run the vault's own rules
 * (`stakeRuleCheck` mirrors them) and is not a CPI (budgets come from the
 * signed simulation).
 */
export async function dryRunInner(rpc: Pick<Rpc, 'simulateTransaction'>, inner: InnerIx[], relayer: Uint8Array, vault: Uint8Array, opts: { minContextSlot: number; limits?: Limits; recentBlockhash?: Uint8Array }): Promise<DryRunResult> {
  const message = compileV1(relayer, dryRunInstructions(inner, vault), opts.recentBlockhash ?? new Uint8Array(32), opts.limits ?? DRY_RUN_LIMITS);
  checkV1(message);
  const bytes = serializeV1(message);
  const wire = concat(bytes, new Uint8Array(SIGNATURE_SIZE * message.numRequiredSignatures));
  const sim: SimulationResult = await rpc.simulateTransaction(wire, { replaceRecentBlockhash: true, minContextSlot: opts.minContextSlot });
  return { ok: sim.err === null || sim.err === undefined, err: sim.err ?? null, logs: sim.logs, unitsConsumed: sim.unitsConsumed, contextSlot: sim.slot, wire };
}

/** Sign only if the dry run succeeded and ran in the snapshot's epoch (docs/19 6.3 item 6); `schedule` is the bank's, from `epochScheduleFromEpochInfo`, never the sysvar account alone. */
export function checkDryRun(result: DryRunResult, snapshotSlot: bigint, schedule: EpochSchedule): Checked {
  if (!result.ok) return refuse({ kind: 'DryRunFailed', err: result.err });
  const snapshotEpoch = epochOfSlot(snapshotSlot, schedule);
  const contextEpoch = epochOfSlot(BigInt(result.contextSlot), schedule);
  if (snapshotEpoch !== contextEpoch) return refuse({ kind: 'EpochChanged', snapshotEpoch, contextEpoch });
  return ok(undefined);
}

// --------------------------------------------- the structural-rule mirror

/** An account in the pre-state the rules read (owner, lamports, data); an address not listed reads as absent (System-owned, empty, 0 lamports), as the runtime presents it. */
export interface RuleAccount {
  address: Uint8Array;
  owner: Uint8Array;
  lamports: bigint;
  data: Uint8Array;
}

export interface RuleAux {
  address: Uint8Array;
  isWritable: boolean;
  isSigner: boolean;
}

/** The Execute header as the rules see it: raw flags (unknown bits are refused) and the clauses, with the aux accounts in order. */
export interface RuleHeader {
  flags: number;
  executor?: Uint8Array | null;
  settle?: SettleSpec | null;
  lamportMove?: { auxIndex: number; lamports: bigint } | null;
  boundedIx?: number | null;
  aux: RuleAux[];
}

/**
 * The program's answer as far as the client can see before signing:
 * `'lands'` (every structural rule passes; the CPIs may still fail inside the
 * target program), a `VaultError` code (11 NotAllowed, 23 StakeRule, 15
 * ForeignVaultAccount, 4 ExecutorNotSigner, 8 BadAuxIndex, 14 SettleFailed,
 * 22 FeeAccount, 19 LamportMoveConflict, and the walk's 12, 13, 16, 17), or a
 * plain `ProgramError` name for a malformed header.
 */
export type RuleOutcome = 'lands' | { vaultError: number } | { programError: string };

export const VAULT_ERROR = {
  ExecutorNotSigner: 4,
  BadAuxIndex: 8,
  NotAllowed: 11,
  SettleRequired: 12,
  SlotMismatch: 13,
  SettleFailed: 14,
  ForeignVaultAccount: 15,
  TooManyAccounts: 16,
  TooManyInstructions: 17,
  LamportMoveConflict: 19,
  FeeAccount: 22,
  StakeRule: 23,
} as const;

class RuleStop {
  constructor(readonly outcome: RuleOutcome) {}
}
const vaultErr = (code: number): never => {
  throw new RuleStop({ vaultError: code });
};
const programErr = (name: string): never => {
  throw new RuleStop({ programError: name });
};

/** One row of the Stake slot table (allowlist.rs `StakeSlots`, docs/19 4.4.4). */
interface StakeSlots {
  dataLen: number;
  metas: number;
  vaultMask: number;
  recipient: boolean;
  ownMask: number;
  allocatedAt: number | null;
}
const row = (dataLen: number, metas: number, vaultMask: number, more: Partial<StakeSlots> = {}): StakeSlots => ({ dataLen, metas, vaultMask, recipient: false, ownMask: 0, allocatedAt: null, ...more });
/** p-token `UnwrapLamports` (docs/19 4.4.2): 10 bytes, `[source, destination, vault]`, the vault at 2 only. */
const UNWRAP_SLOTS = row(10, 3, 0b100);
function stakeSlots(t: number): StakeSlots | null {
  switch (t) {
    case STAKE_INITIALIZE_CHECKED:
      return row(4, 3, 0b110, { allocatedAt: 0 });
    case STAKE_DELEGATE:
      return row(4, 3, 0b100);
    case STAKE_DEACTIVATE:
      return row(4, 2, 0b010);
    case STAKE_WITHDRAW:
      return row(12, 3, 0b100, { recipient: true });
    case STAKE_SPLIT:
      return row(12, 3, 0b100, { ownMask: 0b001, allocatedAt: 1 });
    case STAKE_MERGE:
      return row(4, 3, 0b100, { ownMask: 0b011 });
    default:
      return null;
  }
}

type Target = { kind: 'Jupiter'; slots: { authority: number; src: number; dst: number } } | { kind: 'Token' } | { kind: 'Unwrap' } | { kind: 'Stake'; slots: StakeSlots } | { kind: 'System' };

/** allowlist.rs `classify`: the target by program and leading bytes, or 11. */
function classifyTarget(program: Uint8Array, data: Uint8Array): Target {
  if (equalBytes(program, JUPITER_V6)) {
    if (data.length < 8) return vaultErr(VAULT_ERROR.NotAllowed);
    const s = jupiterSlots(data.subarray(0, 8));
    return s ? { kind: 'Jupiter', slots: s } : vaultErr(VAULT_ERROR.NotAllowed);
  }
  if (equalBytes(program, TOKEN_PROGRAM) || equalBytes(program, TOKEN_2022_PROGRAM)) {
    const t = data[0];
    if (t === TOKEN_TRANSFER_CHECKED || t === TOKEN_SYNC_NATIVE || t === TOKEN_CLOSE_ACCOUNT) return { kind: 'Token' };
    if (t === TOKEN_UNWRAP_LAMPORTS && equalBytes(program, TOKEN_PROGRAM)) return { kind: 'Unwrap' };
    return vaultErr(VAULT_ERROR.NotAllowed);
  }
  if (equalBytes(program, STAKE_PROGRAM)) {
    if (data.length < 4) return vaultErr(VAULT_ERROR.NotAllowed);
    const s = stakeSlots(view(data).getUint32(0, true));
    return s ? { kind: 'Stake', slots: s } : vaultErr(VAULT_ERROR.NotAllowed);
  }
  if (equalBytes(program, SYSTEM_PROGRAM)) {
    if (data.length >= 4 && data[0] === SYSTEM_ALLOCATE_WITH_SEED && data[1] === 0 && data[2] === 0 && data[3] === 0) return { kind: 'System' };
    return vaultErr(VAULT_ERROR.NotAllowed);
  }
  return vaultErr(VAULT_ERROR.NotAllowed);
}

interface TokenFields {
  program: Uint8Array;
  mint: Uint8Array;
  owner: Uint8Array;
  amount: bigint;
  lamports: bigint;
  nativeReserve: bigint | null;
  delegateNone: boolean;
  closeAuthorityNone: boolean;
}

/** execute.rs `token_fields`: a token account by shape (165 bytes under SPL Token; 165, or longer with marker 2 at byte 165, under Token-2022; never a 355-byte multisig). */
function tokenFields(a: RuleAccount): TokenFields | null {
  const len = a.data.length;
  const isToken = equalBytes(a.owner, TOKEN_PROGRAM) || equalBytes(a.owner, TOKEN_2022_PROGRAM);
  if (!isToken || len < 165 || len === 355) return null;
  if (equalBytes(a.owner, TOKEN_PROGRAM) && len !== 165) return null;
  const d = a.data;
  if (len > 165 && d[165] !== 2) return null;
  const isZero4 = (at: number) => d[at] === 0 && d[at + 1] === 0 && d[at + 2] === 0 && d[at + 3] === 0;
  const native = d[109] === 1 && d[110] === 0 && d[111] === 0 && d[112] === 0;
  return { program: a.owner, mint: d.slice(0, 32), owner: d.slice(32, 64), amount: u64At(d, 64), lamports: a.lamports, nativeReserve: native ? u64At(d, 113) : null, delegateNone: isZero4(72), closeAuthorityNone: isZero4(129) };
}

/** execute.rs `mint_extensions_acceptable`. */
function mintAcceptable(d: Uint8Array): boolean {
  if (d.length < 82 || d[45] !== 1 || d.length === 355) return false;
  if (d.length === 82) return true;
  if (d.length <= 165 || d[165] !== 1) return false;
  let pos = 166;
  while (pos + 4 <= d.length) {
    const t = d[pos]! | (d[pos + 1]! << 8);
    const len = d[pos + 2]! | (d[pos + 3]! << 8);
    if (t === 0) break;
    if (t === 4 || t === 12 || t === 14 || t === 24) return false;
    pos += 4 + len;
  }
  return true;
}

/**
 * docs/19 4.4 as implemented in program/src/instructions/execute.rs and
 * allowlist.rs: predict the program's answer to an Execute from its header,
 * aux accounts, inner instructions and the pre-state `accounts` (owner,
 * lamports, data of every account the rules read: the Withdraw recipient,
 * Split and Merge stake accounts, the UnwrapLamports destination, the SETTLE
 * token accounts, mints and fee account, and every account I-S4 scans), in
 * the precedence of docs/19 4.4.6: header parse; EXECUTOR (4); for SETTLE the
 * in/out (8), token-account and mint (14) and fee-account (22) checks; per
 * inner instruction its structure (16), classify (11), the Jupiter slots (12,
 * 13) and its stake table (23); the staking-Execute rules (23);
 * LAMPORT_MOVE (8, 19); the SETTLE account scan with I-S4 (15).
 *
 * Not modelled (they need the vault's own state, the clock or the
 * signature): last leaf 21, Reopen 7, the signature (1, 2, 3, 10), the rent
 * floor of a lamport move (5), the CPIs themselves and the SETTLE
 * post-checks (14 after the CPIs). `treasury` (jupiter.ts `treasuryAddress`)
 * is needed only for a SETTLE header.
 */
export function stakeRuleCheck(header: RuleHeader, inner: InnerIx[], accounts: RuleAccount[], ctx: { vault: Uint8Array; treasury?: Uint8Array }): { outcome: RuleOutcome } {
  try {
    runRules(header, inner, accounts, ctx.vault, ctx.treasury);
    return { outcome: 'lands' };
  } catch (e) {
    if (e instanceof RuleStop) return { outcome: e.outcome };
    throw e;
  }
}

function runRules(header: RuleHeader, inner: InnerIx[], accounts: RuleAccount[], vault: Uint8Array, treasury: Uint8Array | undefined): void {
  const known = new Map(accounts.map((a) => [hex(a.address), a] as const));
  const acct = (address: Uint8Array): RuleAccount => known.get(hex(address)) ?? { address, owner: SYSTEM_PROGRAM, lamports: 0n, data: new Uint8Array(0) };
  const flags = header.flags;
  const aux = header.aux;

  // Header parse (ExecHeader::parse).
  if (flags & ~0x1f) programErr('InvalidInstructionData');
  if (aux.length > MAX_AUX) programErr('InvalidInstructionData');
  const settle = flags & FLAGS.SETTLE ? (header.settle ?? null) : null;
  if (flags & FLAGS.SETTLE && !settle) throw new Error('stakeRuleCheck: SETTLE flag without settle clause');
  const bounded = flags & FLAGS.BOUNDED ? (header.boundedIx ?? null) : null;
  if (bounded !== null && !settle) programErr('InvalidInstructionData');
  const lamportMove = flags & FLAGS.LAMPORT_MOVE ? (header.lamportMove ?? null) : null;
  if (flags & FLAGS.LAMPORT_MOVE && !lamportMove) throw new Error('stakeRuleCheck: LAMPORT_MOVE flag without its clause');
  if (inner.length > MAX_INNER_IX) vaultErr(VAULT_ERROR.TooManyInstructions);

  // The inner range: per inner instruction, its program then its metas.
  const range: Uint8Array[] = [];
  for (const ix of inner) range.push(ix.program, ...ix.metas.map((m) => m.pubkey));
  const findAccount = (address: Uint8Array): RuleAccount | null => {
    const hit = [...aux.map((a) => a.address), ...range].find((a) => equalBytes(a, address));
    return hit ? acct(hit) : null;
  };

  // EXECUTOR (4).
  if (flags & FLAGS.EXECUTOR) {
    const executor = header.executor;
    if (!executor) throw new Error('stakeRuleCheck: EXECUTOR flag without an executor');
    if (!aux.some((a) => a.isSigner && equalBytes(a.address, executor))) vaultErr(VAULT_ERROR.ExecutorNotSigner);
  }

  // SETTLE before the walk: in/out (8), token accounts and mints (14), fee account (22).
  if (settle) {
    const [i, o] = [settle.inIndex, settle.outIndex];
    if (i >= aux.length || o >= aux.length || i === o) vaultErr(VAULT_ERROR.BadAuxIndex);
    const fin = tokenFields(acct(aux[i]!.address)) ?? vaultErr(VAULT_ERROR.SettleFailed);
    const fout = tokenFields(acct(aux[o]!.address)) ?? vaultErr(VAULT_ERROR.SettleFailed);
    if (!equalBytes(fin.owner, vault) || !equalBytes(fout.owner, vault) || !equalBytes(fin.mint, settle.inMint) || !equalBytes(fout.mint, settle.outMint)) vaultErr(VAULT_ERROR.SettleFailed);
    const inMint = findAccount(settle.inMint) ?? vaultErr(VAULT_ERROR.SettleFailed);
    const outMint = findAccount(settle.outMint) ?? vaultErr(VAULT_ERROR.SettleFailed);
    if (!equalBytes(inMint.owner, fin.program) || !mintAcceptable(inMint.data)) vaultErr(VAULT_ERROR.SettleFailed);
    if (!equalBytes(outMint.owner, fout.program) || !mintAcceptable(outMint.data)) vaultErr(VAULT_ERROR.SettleFailed);
    if (!treasury) throw new Error('stakeRuleCheck: a SETTLE header needs the treasury address');
    const side = feeSide(settle.inMint, settle.outMint);
    const [feeMint, feeProgram, other] = side === 'output' ? [settle.outMint, fout.program, i] : [settle.inMint, fin.program, o];
    let found = false;
    for (let k = 0; k < aux.length && !found; k++) {
      if (k === other) continue;
      const f = tokenFields(acct(aux[k]!.address));
      found = !!f && equalBytes(f.owner, treasury) && equalBytes(f.mint, feeMint) && equalBytes(f.program, feeProgram) && f.delegateNone && f.closeAuthorityNone && aux[k]!.isWritable;
    }
    if (!found || !findAccount(feeProgram)) vaultErr(VAULT_ERROR.FeeAccount);
  }

  // The walk.
  let staking = false;
  let stakeSeen = false;
  let otherToken = false;
  const unwrapDst: Uint8Array[] = [];
  const allocated: Uint8Array[] = [];
  let pending: Uint8Array | null = null;
  let hasJupiter = false;
  inner.forEach((ix, k) => {
    const metas = ix.metas.map((m) => m.pubkey);
    if (metas.length > MAX_INNER_ACCOUNTS) vaultErr(VAULT_ERROR.TooManyAccounts);
    const target = classifyTarget(ix.program, ix.data);
    if (target.kind === 'Jupiter') {
      if (!settle) vaultErr(VAULT_ERROR.SettleRequired);
      const s = target.slots;
      const n = metas.length;
      if (s.authority >= n || s.src >= n || s.dst >= n) vaultErr(VAULT_ERROR.SlotMismatch);
      if (!equalBytes(metas[s.authority]!, vault) || !equalBytes(metas[s.src]!, aux[settle!.inIndex]!.address) || !equalBytes(metas[s.dst]!, aux[settle!.outIndex]!.address)) vaultErr(VAULT_ERROR.SlotMismatch);
      hasJupiter = true;
    } else if (bounded === k) {
      vaultErr(VAULT_ERROR.NotAllowed);
    }
    // StakeWalk::step: the 4.4.3 pairing, then the instruction's own table.
    if (pending) {
      const p: Uint8Array = pending;
      pending = null;
      const at = target.kind === 'Stake' ? target.slots.allocatedAt : null;
      if (at === null || !metas[at] || !equalBytes(metas[at]!, p)) vaultErr(VAULT_ERROR.StakeRule);
    }
    switch (target.kind) {
      case 'Stake':
        checkSlots(target.slots, metas, ix.data, vault, acct);
        staking = true;
        stakeSeen = true;
        break;
      case 'System':
        checkSystem(metas, ix.data, vault);
        staking = true;
        allocated.push(metas[0]!);
        pending = metas[0] ?? null;
        break;
      case 'Unwrap':
        // Never after a Stake instruction: the destination's state is read before the CPIs, and a
        // Merge, a full Split or a full Withdraw earlier in the Execute drains it to 0 bytes first, so
        // the lamports would be stranded where nobody can sign (A18, docs/19 4.4.2; R74).
        if (ix.data[1] !== 1 || stakeSeen) vaultErr(VAULT_ERROR.StakeRule);
        checkSlots(UNWRAP_SLOTS, metas, ix.data, vault, acct);
        unwrapDst.push(metas[1]!);
        break;
      case 'Token':
        otherToken ||= ix.data[0] !== TOKEN_SYNC_NATIVE;
        break;
      case 'Jupiter':
        break;
    }
  });
  if (bounded !== null && bounded >= inner.length) programErr('InvalidInstructionData');
  if (hasJupiter && !settle) vaultErr(VAULT_ERROR.SettleRequired);

  // StakeWalk::finish (4.4.2, 4.4.3, 4.4.5 rules 1-3).
  if (pending || (unwrapDst.length > 0 && !staking)) vaultErr(VAULT_ERROR.StakeRule);
  if (staking && (otherToken || flags & (FLAGS.SETTLE | FLAGS.LAMPORT_MOVE))) vaultErr(VAULT_ERROR.StakeRule);
  for (const dst of unwrapDst) {
    const isAllocated = allocated.some((a) => equalBytes(a, dst));
    if (!isAllocated && authorityBitsOf(acct(dst), vault) !== OWN_STAKE) vaultErr(VAULT_ERROR.StakeRule);
  }

  // LAMPORT_MOVE (8, 19).
  if (lamportMove) {
    if (lamportMove.auxIndex >= aux.length) vaultErr(VAULT_ERROR.BadAuxIndex);
    const dst = aux[lamportMove.auxIndex]!.address;
    for (const ix of inner) {
      const hasVault = ix.metas.some((m) => equalBytes(m.pubkey, vault));
      const hasDst = ix.metas.some((m) => equalBytes(m.pubkey, dst));
      if (hasVault !== hasDst) vaultErr(VAULT_ERROR.LamportMoveConflict);
    }
  }

  // SETTLE account scan with I-S4 (15).
  if (settle) {
    const inAddr = aux[settle.inIndex]!.address;
    const outAddr = aux[settle.outIndex]!.address;
    for (const address of [...aux.map((a) => a.address), ...range]) {
      const a = acct(address);
      if (authorityBitsOf(a, vault) & 3) vaultErr(VAULT_ERROR.ForeignVaultAccount);
      if (equalBytes(address, inAddr) || equalBytes(address, outAddr)) continue;
      const f = tokenFields(a);
      if (f && equalBytes(f.owner, vault)) vaultErr(VAULT_ERROR.ForeignVaultAccount);
    }
  }
}

const authorityBitsOf = (a: RuleAccount, vault: Uint8Array): number => authorityBits(a.owner, a.data, vault);

/** execute.rs `check_slots`: exact data length and meta count, the vault exactly at the row's positions, the Withdraw recipient, the Split/Merge own-stake accounts (23). */
function checkSlots(s: StakeSlots, metas: Uint8Array[], data: Uint8Array, vault: Uint8Array, acct: (a: Uint8Array) => RuleAccount): void {
  if (data.length !== s.dataLen || metas.length !== s.metas) vaultErr(VAULT_ERROR.StakeRule);
  metas.forEach((m, k) => {
    const isVault = equalBytes(m, vault);
    if (((s.vaultMask >> k) & 1) === 1 !== isVault && !(s.recipient && k === 1)) vaultErr(VAULT_ERROR.StakeRule);
    if ((s.ownMask >> k) & 1 && authorityBitsOf(acct(m), vault) !== OWN_STAKE) vaultErr(VAULT_ERROR.StakeRule);
  });
  if (s.recipient && metas[1] && !equalBytes(metas[1], vault)) {
    const f = tokenFields(acct(metas[1])) ?? vaultErr(VAULT_ERROR.StakeRule);
    if (!equalBytes(f.program, TOKEN_PROGRAM) || !equalBytes(f.owner, vault) || f.nativeReserve === null || !f.delegateNone || !f.closeAuthorityNone) vaultErr(VAULT_ERROR.StakeRule);
  }
}

/** execute.rs `check_system`: the AllocateWithSeed layout of docs/19 4.4.3 and its metas `[new (not the vault), vault]` (23). */
function checkSystem(metas: Uint8Array[], data: Uint8Array, vault: Uint8Array): void {
  const l = data.length - 84;
  const good =
    l >= 1 &&
    l <= 32 &&
    u64At(data, 36) === BigInt(l) &&
    equalBytes(data.subarray(4, 36), vault) &&
    u64At(data, 44 + l) === STAKE_ACCOUNT_LEN &&
    equalBytes(data.subarray(52 + l), STAKE_PROGRAM) &&
    metas.length === 2 &&
    !equalBytes(metas[0]!, vault) &&
    equalBytes(metas[1]!, vault);
  if (!good) vaultErr(VAULT_ERROR.StakeRule);
}

/** Base58 helper for callers that hold addresses as strings. */
export const stakeAddressBase58 = (vault: Uint8Array, n: number): string => toBase58(stakeAddress(vault, n));
